import { State } from '@progress/kendo-data-query';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useCheckbookCategories, {
  EditableColumnCategories,
} from 'src/assets/static/view_checkbook/useCheckbookCategories';
import ColumnEditorDropDown from 'src/components/ColumnEditorDropDown';
import ExportExcel, { defaultExcelOption } from 'src/components/ExportExcel';
import useCompany from 'src/hooks/useCompany';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import useYearView from 'src/hooks/useYearView';
import { SetState } from 'src/types/generic';
import { modifyRequest } from 'src/utils/requestInterceptor';
import DeleteGLCode from './DeleteGLCode';
import ViewCheckbookExcelDropdown, { Category } from './NestedExcelDropdown';
import UnDeleteGLCode from './UnDeleteGLCode';
import { formatCheckbookExcelData } from './index';
import AddGlCode from './year_view/AddGlCode';
import ClearFilters from './year_view/ClearFilters';

interface GridToolbarProps {
  excelExport: (data: any[], isIncluded: boolean) => void;
  setLoading: SetState<boolean>;
  setSelectionFilter: React.Dispatch<any>;
  setState: SetState<State>;
  selectionFilter: any;
  allPageData: any[];
  rows: any[];
}

const checkbookYearOptions: Category[] = [
  {
    content: 'Include Departments',
    id: 1,
    items: [
      { content: 'Current Page', id: 2 },
      { content: 'All Pages', id: 3 },
    ],
    parent: true,
  },
  {
    content: 'Exclude Departments',
    id: 4,
    items: [
      { content: 'Current Page', id: 5 },
      { content: 'All Pages', id: 6 },
    ],
    parent: true,
  },
];

const ViewCheckbookYearTableToolbar: FC<GridToolbarProps> = ({
  excelExport,
  rows,
  setLoading,
  selectionFilter,
  setSelectionFilter,
  setState,
  allPageData,
}) => {
  const categories = useCheckbookCategories();
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const {
    setGlCodesBudgetByYear,
    selectedYearDepartment,
    hasEditGLCodePermission,
    confirmBeforeProceeding,
    isEditMode,
    editingBudgetYearDataWithErrors,
    isSelectedFYPeriodsClosed,
  } = useViewCheckbook();

  const { yearViewData, editableColumnDropdownData, setEditableColumnDropdownData } = useYearView();

  const {
    company: { companyName },
  } = useCompany();

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      if (selectedOption.value === 1) {
        setLoading(true);
        excelExport(
          rows?.map((row) => ({ ...row, companyName })),
          false
        );
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption.value === 2) {
        setLoading(true);
        excelExport(
          allPageData?.map((row) => ({ ...row, companyName })),
          false
        );
        setExcelOption(defaultExcelOption);
        setLoading(false);
      }
    },
    [allPageData, companyName, excelExport, rows, setLoading]
  );

  const selectedRows = useMemo(
    () =>
      (isEditMode ? editingBudgetYearDataWithErrors : yearViewData).filter((item) => item.selected),
    [editingBudgetYearDataWithErrors, isEditMode, yearViewData]
  );

  const handleTreeDropdownExcelChange = useCallback(
    (id: number) => {
      const selectedOption = id;

      if (selectedOption === 2) {
        setLoading(true);
        const formattedData = formatCheckbookExcelData(
          rows.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 3) {
        setLoading(true);
        const formattedData = formatCheckbookExcelData(
          allPageData?.map((row) => ({ ...row, companyName }))
        );
        excelExport(formattedData, true);
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 5) {
        setLoading(true);
        excelExport(
          rows.map((row) => ({ ...row, companyName })),
          false
        );
        setExcelOption(defaultExcelOption);
        setLoading(false);
      } else if (selectedOption === 6) {
        setLoading(true);
        excelExport(
          allPageData?.map((row) => ({ ...row, companyName })),
          false
        );
        setExcelOption(defaultExcelOption);
        setLoading(false);
      }
    },
    [companyName, excelExport, allPageData, rows, setLoading]
  );

  const handleCategoryChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e;
      confirmBeforeProceeding(() => {
        setGlCodesBudgetByYear((years) => years.map((glCode) => ({ ...glCode, selected: false })));
        setSelectionFilter(value);
        setState((prev) => {
          if (modifyRequest(prev).filters.length) {
            toast.info('Preset filter applied. User-applied filters cleared.');
          }
          return {
            ...prev,
            filter: {
              logic: 'and',
              filters: [],
            },
          };
        });
      });
    },
    [confirmBeforeProceeding, setGlCodesBudgetByYear, setSelectionFilter, setState]
  );

  const handleEditColumnChange = useCallback(
    (dataItem: EditableColumnCategories[]) => {
      setEditableColumnDropdownData(dataItem);
    },
    [setEditableColumnDropdownData]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-1">
        <ClearFilters />
        {hasEditGLCodePermission && !isSelectedFYPeriodsClosed && (
          <>
            <AddGlCode />
            <DeleteGLCode selectedRows={selectedRows} />
            <UnDeleteGLCode selectedRows={selectedRows} />
          </>
        )}
      </div>

      <div className="flex flex-row gap-2">
        {selectedYearDepartment?.departmentName === 'All Departments' ? (
          <ViewCheckbookExcelDropdown
            handleExcelChange={handleTreeDropdownExcelChange}
            options={checkbookYearOptions}
          />
        ) : (
          <ExportExcel excelOption={excelOption} onExcelChanged={handleExcelChange} width={200} />
        )}
        <DropDownList
          id="view-drop-down-list"
          style={{ width: 200 }}
          data={categories}
          textField="label"
          dataItemKey="value"
          value={selectionFilter}
          onChange={handleCategoryChange}
        />
        <ColumnEditorDropDown onChange={handleEditColumnChange} data={editableColumnDropdownData} />
      </div>
    </div>
  );
};
export default ViewCheckbookYearTableToolbar;
